import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Screen } from '@tempmee/react';
import { BackButton, CenteredSpinner } from '~/commonComponents';
import { LocationLoadError } from '~/components/shared/LocationLoadError';
import { useOfficeProfile } from '~/domain/LocationSettings';
import { useUserSelector } from '~/hooks/useUserSelector';
import { useViewedOfficeScope } from '~/hooks/useViewedOfficeScope';
import { getViewedOfficeId } from '~/utils/viewedOfficeScope';
import { Content } from './components/Content';
import { Title } from './components/Title';

/**
 * Worker Type Setting for the selected office, or for the location a Manage row
 * opened it for (`?officeId=`, GOTU-19472): that location's record, read with its
 * own `x-office`, is the one every part of the page describes and saves to,
 * while the office selected in the header stays put.
 */
export function WorkerClassification({ isRegistration }) {
  const scope = useViewedOfficeScope();
  const headerOfficeId = useUserSelector()?.id;
  const selectedChildOffice = useSelector((state) => state.user.selectedChildOffice);
  const locationId = getViewedOfficeId(scope);
  const viewedOfficeId = locationId !== headerOfficeId ? locationId : undefined;
  const { data: viewedOffice, isError, isPlaceholderData } = useOfficeProfile(viewedOfficeId ?? '');
  const office = viewedOfficeId ? viewedOffice : selectedChildOffice;
  const isViewedOfficeError = !!viewedOfficeId && isError;
  // The location's own record, not a placeholder, decides its status and its save.
  const isLoading =
    !scope || (!!viewedOfficeId && !isError && (!viewedOffice || isPlaceholderData));
  const isReady = !isLoading && !isViewedOfficeError;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Screen variant="standard">
      <Screen.Header start={<BackButton />} title="Worker Type Setting">
        {isReady && (
          <Title isRegistration={isRegistration} showHeading={false} office={office} />
        )}
      </Screen.Header>
      <Screen.Body>
        {isViewedOfficeError && <LocationLoadError />}
        {isLoading && <CenteredSpinner />}
        {isReady && (
          <Content
            isRegistration={isRegistration}
            office={office}
            viewedOfficeId={viewedOfficeId}
          />
        )}
      </Screen.Body>
    </Screen>
  );
}
